import React, { useEffect, useMemo, useState } from 'react';
import './B2bPortalReportModal.css';

// the server sends the patient row with whatever casing the stored procedure used
export const pick = (obj, ...names) => {
  if (!obj) return undefined;
  const lower = {};
  Object.keys(obj).forEach((k) => { lower[k.toLowerCase()] = obj[k]; });
  for (const n of names) {
    const v = lower[n.toLowerCase()];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return undefined;
};

export const esc = (v) =>
  String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// the master stores a data type ("Numeric") where a unit would go
const cleanUnit = (u) => (u && !/^(numeric|text|alphanumeric|date|paragraph|string)$/i.test(String(u).trim()) ? u : '');

const isApproved = (t) => t.approved === true || Number(t.approved) === 1;
const isAbnormal = (o) => o.flag && String(o.flag).toUpperCase() !== 'N';
const testName = (t) => t.investigationName || t.itemName || 'Investigation';
const obsOf = (t) => (Array.isArray(t.observations) ? t.observations : []);

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

function buildPrintHtml(patient, tests, withHeader) {
  const name = pick(patient, 'patientName', 'pName');
  const isCoBrand = Boolean(pick(patient, 'enableCoBranding'));
  const partyName = pick(patient, 'partyName') || 'Partner Clinic';
  const logoUrl = pick(patient, 'coBrandingLogoUrl');
  const tagline = pick(patient, 'coBrandingTagline') || 'Referred By / Collection Partner';
  const partyAddress = pick(patient, 'coBrandingAddress');
  const partyContact = pick(patient, 'coBrandingContact');
  const centreName = pick(patient, 'centreName') || 'Central Laboratory Report';

  const rows = tests
    .map((t) => {
      const obs = obsOf(t);
      const body = obs.length
        ? `<table><thead><tr><th>Parameter</th><th>Result</th><th>Unit</th><th>Reference range</th><th>Flag</th></tr></thead><tbody>${obs
            .map(
              (o) =>
                `<tr class="${isAbnormal(o) ? 'abn' : ''}"><td>${esc(o.observationName)}</td><td><b>${esc(o.value)}</b></td><td>${esc(cleanUnit(o.unit))}</td><td>${esc(o.referenceRange)}${o.method ? `<br><small>${esc(o.method)}</small>` : ""}</td><td>${isAbnormal(o) ? esc(o.flag) : ''}</td></tr>`
            )
            .join('')}</tbody></table>`
        : '<p class="note">The result values of this test are in the signed report held by the laboratory. Please contact the laboratory for a copy.</p>';
      return `<section><h3>${esc(testName(t))}</h3><div class="meta">Sample ${esc(t.barcodeNo || '—')} &bull; Approved by ${esc(t.approvedBy || '—')} &bull; ${esc(fmtDate(t.approvedDate))}</div>${body}</section>`;
    })
    .join('');
  return `<!doctype html><html><head><meta charset="utf-8"><title>Laboratory report ${esc(pick(patient, 'ledgerTransactionNo'))}</title>
<style>
@page { size: A4; margin: 14mm; }
body { font-family: 'Segoe UI', Arial, sans-serif; color: #0f172a; font-size: 12px; }
header { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 3px solid #1e3a8a; padding-bottom: 8px; margin-bottom: 12px; }
header h1 { margin: 0; font-size: 20px; color: #1e3a8a; letter-spacing: .5px; }
header span { color: #64748b; font-size: 11px; }
.cobrand-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 3px solid #0284c7; padding-bottom: 10px; margin-bottom: 14px; }
.cobrand-left { display: flex; align-items: center; gap: 12px; }
.cobrand-logo { max-height: 52px; max-width: 140px; object-fit: contain; }
.cobrand-right { text-align: right; }
.cobrand-right h1 { margin: 0; font-size: 17px; color: #1e3a8a; }
.pt { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px 16px; padding: 10px 12px; border: 1px solid #cbd5e1; border-radius: 6px; margin-bottom: 14px; }
.pt b { color: #0f172a; } .pt div { color: #475569; }
section { margin-bottom: 14px; break-inside: avoid; }
h3 { margin: 0 0 2px; font-size: 14px; color: #1e3a8a; }
.meta { color: #64748b; font-size: 11px; margin-bottom: 6px; }
table { width: 100%; border-collapse: collapse; }
th { text-align: left; background: #e2e8f0; padding: 6px 8px; font-size: 11px; }
td { padding: 6px 8px; border-bottom: 1px solid #e2e8f0; }
tr.abn td { color: #b91c1c; font-weight: 700; }
.note { color: #64748b; font-style: italic; }
/* pre-printed letterhead paper: keep the top free */
.letterhead-gap { height: 38mm; }
footer { margin-top: 18px; padding-top: 8px; border-top: 1px solid #cbd5e1; color: #64748b; font-size: 10px; display: flex; justify-content: space-between; }
</style></head><body>
${withHeader ? (
  isCoBrand ? `
  <header class="cobrand-header">
    <div class="cobrand-left">
      ${logoUrl ? `<img src="${esc(logoUrl)}" class="cobrand-logo" alt="Partner Logo" />` : ''}
      <div>
        <h2 style="margin:0;font-size:16px;color:#0f172a;">${esc(partyName)}</h2>
        <div style="font-size:11px;color:#0284c7;font-weight:600;">${esc(tagline)}</div>
        ${partyAddress ? `<div style="font-size:10px;color:#64748b;">${esc(partyAddress)}</div>` : ''}
        ${partyContact ? `<div style="font-size:10px;color:#64748b;">${esc(partyContact)}</div>` : ''}
      </div>
    </div>
    <div class="cobrand-right">
      <h1>${esc(centreName)}</h1>
      <span>Accredited Reference Laboratory &bull; Printed ${esc(fmtDate(new Date()))}</span>
    </div>
  </header>
  ` : `
  <header><div><h1>${esc(centreName)}</h1><span>Laboratory investigation report</span></div><span>Printed ${esc(fmtDate(new Date()))}</span></header>
  `
) : `<div class="letterhead-gap"></div>`}
<div class="pt">
<div>Patient: <b>${esc(name)}</b></div><div>Lab No: <b>${esc(pick(patient, 'ledgerTransactionNo'))}</b></div><div>Booked: <b>${esc(fmtDate(pick(patient, 'bookingDate')))}</b></div>
<div>UHID: <b>${esc(pick(patient, 'uhid', 'patient_ID'))}</b></div><div>Age / Gender: <b>${esc(pick(patient, 'ageYear') ?? '—')} Y / ${esc(pick(patient, 'gender') || '—')}</b></div><div>Referred by: <b>${esc(pick(patient, 'doctorName') || '—')}</b></div>
</div>
${rows}
<footer><span>Only approved results are shown. This is a system generated copy.</span><span>inTeLiSYS</span></footer>
</body></html>`;
}

export function printHtml(html) {
  const frame = document.createElement('iframe');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(frame);
  const doc = frame.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  // wait one frame so the document has laid out before the print dialog opens
  setTimeout(() => {
    frame.contentWindow.focus();
    frame.contentWindow.print();
    setTimeout(() => document.body.removeChild(frame), 1500);
  }, 250);
}

const B2bPortalReportModal = ({ report, onClose }) => {
  const patient = report?.patient;
  const tests = useMemo(() => report?.tests || [], [report]);
  const printable = useMemo(() => tests.filter(isApproved), [tests]);
  const [withHeader, setWithHeader] = useState(true);
  const [picked, setPicked] = useState(() => new Set(printable.map((t) => t.test_ID ?? t.barcodeNo)));

  useEffect(() => {
    setPicked(new Set(printable.map((t) => t.test_ID ?? t.barcodeNo)));
  }, [printable]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!report) return null;

  const keyOf = (t) => t.test_ID ?? t.barcodeNo;
  const toggle = (t) =>
    setPicked((prev) => {
      const next = new Set(prev);
      next.has(keyOf(t)) ? next.delete(keyOf(t)) : next.add(keyOf(t));
      return next;
    });
  const allPicked = printable.length > 0 && printable.every((t) => picked.has(keyOf(t)));
  const chosen = printable.filter((t) => picked.has(keyOf(t)));

  const name = pick(patient, 'patientName', 'pName') || '—';

  return (
    <div className="rpm-overlay" onClick={onClose}>
      <div className="rpm-card" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="rpm-head">
          <div>
            <div className="rpm-eyebrow">Laboratory report</div>
            <h2 className="rpm-title">{name}</h2>
          </div>
          <button type="button" className="rpm-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="rpm-patient">
          <div><span>Lab No</span><b>{pick(patient, 'ledgerTransactionNo') || report.orderRef || '—'}</b></div>
          <div><span>UHID</span><b>{pick(patient, 'uhid', 'patient_ID') || '—'}</b></div>
          <div><span>Age / Gender</span><b>{pick(patient, 'ageYear') ? `${pick(patient, 'ageYear')} Y` : '—'} / {pick(patient, 'gender') || '—'}</b></div>
          <div><span>Booked</span><b>{fmtDate(pick(patient, 'bookingDate'))}</b></div>
          <div><span>Referred by</span><b>{pick(patient, 'doctorName') || '—'}</b></div>
          <div><span>Centre</span><b>{pick(patient, 'centreName') || '—'}</b></div>
        </div>

        {Boolean(pick(patient, 'enableCoBranding')) && (
          <div className="rpm-cobrand-banner" style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '6px', padding: '8px 14px', margin: '0 16px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {pick(patient, 'coBrandingLogoUrl') && (
                <img src={pick(patient, 'coBrandingLogoUrl')} alt="Logo" style={{ maxHeight: '28px', maxWidth: '80px', objectFit: 'contain' }} />
              )}
              <div>
                <span style={{ fontSize: '12px', fontWeight: 700, color: '#0369a1' }}>Co-Branded Report: {pick(patient, 'partyName') || 'Partner Clinic'}</span>
                <span style={{ fontSize: '11px', color: '#64748b', marginLeft: '6px' }}>({pick(patient, 'coBrandingTagline') || 'Referring Centre'})</span>
              </div>
            </div>
            <span style={{ fontSize: '10px', background: '#0284c7', color: '#fff', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>Active Co-Branding</span>
          </div>
        )}

        <div className="rpm-body">
          <div className="rpm-pickbar">
            <label className="rpm-check">
              <input
                type="checkbox"
                checked={allPicked}
                disabled={printable.length === 0}
                onChange={() => setPicked(allPicked ? new Set() : new Set(printable.map(keyOf)))}
              />
              <span>Select all reports</span>
            </label>
            <label className="rpm-check" title="Untick when you print on the laboratory letterhead paper">
              <input type="checkbox" checked={withHeader} onChange={(e) => setWithHeader(e.target.checked)} />
              <span>Print with header</span>
            </label>
            <span className="rpm-count">{chosen.length} of {tests.length} selected for print</span>
          </div>

          {tests.length === 0 && <div className="rpm-empty">No investigations found for this order.</div>}

          {tests.map((t) => {
            const ok = isApproved(t);
            const obs = obsOf(t);
            return (
              <section key={keyOf(t)} className={`rpm-test ${ok ? '' : 'locked'}`}>
                <div className="rpm-test-head">
                  <label className="rpm-check">
                    <input type="checkbox" disabled={!ok} checked={ok && picked.has(keyOf(t))} onChange={() => toggle(t)} />
                    <span className="rpm-test-name">{testName(t)}</span>
                  </label>
                  <span className={`rpm-pill ${ok ? 'ok' : 'wait'}`}>{ok ? '✓ Approved' : '🔒 Awaiting approval'}</span>
                </div>
                <div className="rpm-test-meta">
                  Sample <b>{t.barcodeNo || '—'}</b>
                  {ok && <> &bull; Approved by <b>{t.approvedBy || '—'}</b> &bull; {fmtDate(t.approvedDate)}</>}
                </div>

                {ok && obs.length > 0 && (
                  <div className="rpm-table-wrap">
                    <table className="rpm-table">
                      <thead>
                        <tr><th>Parameter</th><th>Result</th><th>Unit</th><th>Reference range</th><th>Flag</th></tr>
                      </thead>
                      <tbody>
                        {obs.map((o, i) => (
                          <tr key={i} className={isAbnormal(o) ? 'abn' : ''}>
                            <td>{o.observationName}</td>
                            <td><b>{o.value}</b></td>
                            <td>{cleanUnit(o.unit) || '—'}</td>
                            <td>{o.referenceRange || '—'}{o.method && <small className="rpm-method">{o.method}</small>}</td>
                            <td>{isAbnormal(o) ? <span className={`rpm-flag ${o.isCritical ? 'crit' : ''}`}>{o.flag}</span> : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {ok && obs.length === 0 && (
                  <div className="rpm-note">The result values of this test are in the signed report held by the laboratory. Please contact the laboratory for a copy.</div>
                )}
              </section>
            );
          })}
        </div>

        <div className="rpm-foot">
          <button type="button" className="rpm-btn ghost" onClick={onClose}>Close</button>
          {printable.length > 0 ? (
            <button
              type="button"
              className="rpm-btn primary"
              disabled={chosen.length === 0}
              onClick={() => printHtml(buildPrintHtml(patient, chosen, withHeader))}
            >
              🖨️ Print {chosen.length === printable.length ? 'all reports' : `selected (${chosen.length})`}
            </button>
          ) : (
            <span className="rpm-lock">🔒 Print opens once a report is approved</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default B2bPortalReportModal;
